import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  adminSettlementKeys,
  adminSettlementService,
  type SettlementRevolutPayoutResponse,
} from "@/services/adminSettlementService";
import { isTerminalRevolutStatus, type RefreshSettlementDetail } from "./utils";

const REVOLUT_PAYOUT_POLL_INTERVAL_MS = 5000;

/**
 * Polls an open Revolut payout until it reaches a terminal status, mirroring each
 * status into the payee's active-payout cache entry.
 */
export function useRevolutPayoutPolling(
  userId: string,
  payout: SettlementRevolutPayoutResponse | null,
  refreshDetail: RefreshSettlementDetail,
): void {
  const { t } = useTranslation("payments");
  const queryClient = useQueryClient();

  useEffect(() => {
    const row = payout;
    if (!userId || !row || isTerminalRevolutStatus(row.status)) return undefined;

    const tick = async () => {
      try {
        const fresh = await adminSettlementService.getRevolutPayout(row.id);
        if (fresh.status === "COMPLETED") {
          toast.success(t("settlements.detail.revolut.completedToast"));
          queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
            payout: null,
          });
          await refreshDetail({ resetFilters: false });
          return;
        }
        if (isTerminalRevolutStatus(fresh.status)) {
          toast.error(t("settlements.detail.revolut.failedToast"));
          queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
            payout: null,
          });
          return;
        }
        queryClient.setQueryData(adminSettlementKeys.activeRevolutPayout(userId), {
          payout: fresh,
        });
      } catch {
        /* keep polling until terminal */
      }
    };

    const id = window.setInterval(() => void tick(), REVOLUT_PAYOUT_POLL_INTERVAL_MS);
    void tick();
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by payout id/status
  }, [payout?.id, payout?.status, refreshDetail, t, userId, queryClient]);
}
